import { createContext, useContext, useEffect, useState, ReactNode } from "react";

export type SiteLang = "en" | "ru" | "kk";

export const SITE_LANGS: { code: SiteLang; label: string }[] = [
  { code: "en", label: "EN" },
  { code: "ru", label: "RU" },
  { code: "kk", label: "KK" },
];

const STORAGE_KEY = "site-lang";

interface Ctx {
  lang: SiteLang;
  setLang: (l: SiteLang) => void;
}

const SiteLangContext = createContext<Ctx>({ lang: "en", setLang: () => {} });

function readInitial(): SiteLang {
  if (typeof window === "undefined") return "en";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "en" || saved === "ru" || saved === "kk") return saved;
  return "en";
}

export function SiteLangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<SiteLang>(readInitial);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <SiteLangContext.Provider value={{ lang, setLang: setLangState }}>
      {children}
    </SiteLangContext.Provider>
  );
}

export function useSiteLang() {
  return useContext(SiteLangContext);
}

export function LanguageSwitcher({ className = "" }: { className?: string }) {
  const { lang, setLang } = useSiteLang();
  return (
    <div
      className={`flex items-center rounded-full border border-border p-0.5 ${className}`}
      role="group"
      aria-label="Language"
    >
      {SITE_LANGS.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          aria-pressed={lang === code}
          className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
            lang === code
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
